/**
 * Compress image (base64) before upload
 */
export async function compressImage(base64WithOrWithoutPrefix, maxWidth = 640, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, maxWidth / img.width);
      
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Mengembalikan PURE base64 (tanpa prefix)
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      resolve(dataUrl.split(",")[1]); 
    };
    img.onerror = (err) => reject(new Error("Gagal memproses gambar"));
    
    // Tambahkan pengecekan prefix agar tidak double
    if (!base64WithOrWithoutPrefix.startsWith('data:image')) {
        img.src = `data:image/jpeg;base64,${base64WithOrWithoutPrefix}`;
    } else {
        img.src = base64WithOrWithoutPrefix;
    }
  });
}

/**
 * Reverse Geocode - Convert lat/long to address name
 * Menggunakan Nominatim API (OpenStreetMap) - GRATIS
 */
export async function reverseGeocode(latitude, longitude, timeoutMs = 4000) {
  // Layanan publik pihak ketiga (Nominatim): bisa lambat/menggantung. TANPA batas
  // waktu, absen ikut tertahan menunggunya. Alamat hanya pelengkap → menyerah
  // setelah `timeoutMs` dan lanjut tanpa alamat (null).
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    if (!latitude || !longitude) return null;

    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&lang=id`,
      { headers: { "Accept-Language": "id" }, signal: controller.signal }
    );

    if (!response.ok) throw new Error("Geocoding failed");
    
    const data = await response.json();
    
    // Extract address components
    const address = data.address;
    if (!address) return null;
    
    // Format: "Jalan/Jl, Kelurahan, Kecamatan, Kota/Kabupaten"
    const parts = [];
    
    if (address.road || address.street) {
      parts.push(address.road || address.street);
    }
    
    if (address.village || address.suburb) {
      parts.push(address.village || address.suburb);
    }
    
    if (address.district) {
      parts.push(address.district);
    }
    
    if (address.city || address.town) {
      parts.push(address.city || address.town);
    }
    
    return parts.filter(Boolean).join(", ") || data.display_name || null;
    
  } catch (error) {
    console.error("Reverse geocode error:", error);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Get current GPS position
 */
export function getPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation tidak didukung browser ini"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
      ...options,
    });
  });
}

const EARTH_RADIUS_M = 6371000;
const toRad = (deg) => (deg * Math.PI) / 180;

/**
 * Jarak antara dua titik lat/lng (haversine), dalam meter. Dipakai untuk
 * cek geofence absen terhadap `work_location` (radius kantor) sebelum kirim
 * ke server — server tetap otoritas final (lihat backend/src/utils/geo.js).
 */
export function getDistanceMeters(lat1, lon1, lat2, lon2) {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * `work_location` (dari Odoo) hanya jadi geofence aktif bila radius > 0 &
 * lat/lng valid & tidak keduanya 0 (0 = default Odoo utk field belum diisi,
 * bukan koordinat sungguhan). Belum di-set admin → bebas absen di mana saja.
 */
export function isGeofenceActive(loc) {
  if (!loc || typeof loc !== "object") return false;
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  const radius = Number(loc.radius);
  if (!Number.isFinite(radius) || radius <= 0) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

/**
 * Format date to ID locale
 */
export function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Format time HH:mm
 */
export function formatTime(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Format currency IDR
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(amount || 0);
}

/**
 * Get greeting based on hour
 */
export function getGreeting() {
  const h = new Date().getHours();
  if (h < 11) return "Selamat pagi";
  if (h < 15) return "Selamat siang";
  if (h < 18) return "Selamat sore";
  return "Selamat malam";
}
